import { DragDropModule } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { HttpClientModule } from '@angular/common/http'
import { NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { FormlyModule } from '@ngx-formly/core'
import { ChipSelectorModule } from '@stantec/basin/chip-selector'
import { InputWithSelectorModule } from '@stantec/basin/input-with-selector'
import { ListItemModule } from '@stantec/basin/list-item'
import { NumericRangeModule } from '@stantec/basin/numeric-range'
import { SearchboxModule } from '@stantec/basin/searchbox'
import { StepperModule } from '@stantec/basin/stepper'
import { TextboxWithUnitsModule } from '@stantec/basin/textbox-with-units'
import { TimeframePickerModule } from '@stantec/basin/timeframe-picker'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { AvatarModule } from 'primeng/avatar'
import { ButtonModule } from 'primeng/button'
import { CalendarModule } from 'primeng/calendar'
import { CardModule } from 'primeng/card'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipsModule } from 'primeng/chips'
import { ColorPickerModule } from 'primeng/colorpicker'
import { DividerModule } from 'primeng/divider'
import { DropdownModule } from 'primeng/dropdown'
import { FileUploadModule } from 'primeng/fileupload'
import { InputMaskModule } from 'primeng/inputmask'
import { InputNumberModule } from 'primeng/inputnumber'
import { InputSwitchModule } from 'primeng/inputswitch'
import { InputTextModule } from 'primeng/inputtext'
import { InputTextareaModule } from 'primeng/inputtextarea'
import { MenuModule } from 'primeng/menu'
import { MultiSelectModule } from 'primeng/multiselect'
import { OverlayModule } from 'primeng/overlay'
import { PasswordModule } from 'primeng/password'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { RadioButtonModule } from 'primeng/radiobutton'
import { SliderModule } from 'primeng/slider'
import { StepsModule } from 'primeng/steps'
import { FormlyAttendeesComponent } from './formly-attendees/formly-attendees.component'
import { FormlyCheckboxGroupComponent } from './formly-checkbox-group/formly-checkbox-group.component'
import { FormlyCheckboxComponent } from './formly-checkbox/formly-checkbox.component'
import { FormlyChipSelectorComponent } from './formly-chip-selector/formly-chip-selector.component'
import { FormlyChipsComponent } from './formly-chips/formly-chips.component'
import { FormlyColorPickerComponent } from './formly-color-picker/formly-color-picker.component'
import { FormlyContentDropdownComponent } from './formly-content-dropdown/formly-content-dropdown.component'
import { FormlyDateTimePickerComponent } from './formly-date-time-picker/formly-date-time-picker.component'
import { FormlyDatepickerComponent } from './formly-datepicker/formly-datepicker.component'
import { FormlyDividerComponent } from './formly-divider/formly-divider.component'
import { FormlyDropdownComponent } from './formly-dropdown/formly-dropdown.component'
import { FormlyFileUploadBoxComponent } from './formly-file-upload-box/formly-file-upload-box.component'
import { FormlyFileUploadComponent } from './formly-file-upload/formly-file-upload.component'
import { FormlyInputNumberComponent } from './formly-input-number/formly-input-number.component'
import { FormlyInputSwitchComponent } from './formly-input-switch/formly-input-switch.component'
import { FormlyInputWithSelectorComponent } from './formly-input-with-selector/formly-input-with-selector.component'
import { FormlyLabelComponent } from './formly-label/formly-label.component'
import { FormlyMultiselectGroupedComponent } from './formly-multiselect-grouped/formly-multiselect-grouped.component'
import { FormlyMultiselectComponent } from './formly-multiselect/formly-multiselect.component'
import { FormlyNumericRangeComponent } from './formly-numeric-range/formly-numeric-range.component'
import { FormlyPasswordComponent } from './formly-password/formly-password.component'
import { FormlyPhoneComponent } from './formly-phone/formly-phone.component'
import { FormlyRadioButtonComponent } from './formly-radio-button/formly-radio-button.component'
import { FormlyRepeatSingleComponent } from './formly-repeat-single/formly-repeat-single.component'
import { FormlyRepeatComponent } from './formly-repeat/formly-repeat.component'
import { FormlySearchboxComponent } from './formly-searchbox/formly-searchbox.component'
import { FormlySelectOptionsPipe } from './formly-select-options/formly-select-options.pipe'
import { FormlySliderComponent } from './formly-slider/formly-slider.component'
import { FormlyStepperComponent } from './formly-stepper/formly-stepper.component'
import { FormlyTextComponent } from './formly-text/formly-text.component'
import { FormlyTextareaComponent } from './formly-textarea/formly-textarea.component'
import { FormlyTextboxWithCopyComponent } from './formly-textbox-with-copy/formly-textbox-with-copy.component'
import { FormlyTextboxWithUnitsComponent } from './formly-textbox-with-units/formly-textbox-with-units.component'
import { FormlyTextboxComponent } from './formly-textbox/formly-textbox.component'
import { FormlyTimeframePickerComponent } from './formly-timeframe-picker/formly-timeframe-picker.component'
import { FormlyTimepickerComponent } from './formly-timepicker/formly-timepicker.component'
import {
  alphaOnly,
  file,
  inputWithSelector,
  minmax,
  numeric,
  numericRange,
  phone,
  postalCode,
  timeframe,
} from './validator-constants'
import { CustomFormFieldComponent } from './wrappers/custom-form-field/custom-form-field.component'

export const BasinFormlyConfig = {
  validationMessages: [
    { name: 'required', message: 'This field is required.' },
    { name: 'email', message: 'Not a valid email.' },
    { name: 'postalcode', message: postalCode.message },
    { name: 'alphaOnly', message: alphaOnly.message },
    { name: 'phone', message: phone.message },
    { name: 'inputWithSelector', message: inputWithSelector.message },
    { name: 'fileUpload', message: file.message },
    { name: 'timeframe', message: timeframe.message },
    { name: 'minmax', message: minmax.message },
    { name: 'numeric', message: numeric.message },
    { name: 'numericRange', message: numericRange.message },
  ],
  types: [
    {
      name: 'attendees',
      component: FormlyAttendeesComponent,
      wrappers: ['basin-custom-form-field'],
    },
    { name: 'checkboxToggle', component: FormlyCheckboxComponent },
    { name: 'checkboxGroup', component: FormlyCheckboxGroupComponent },
    {
      name: 'chipSelector',
      component: FormlyChipSelectorComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'chips',
      component: FormlyChipsComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'colorpicker',
      component: FormlyColorPickerComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'contentDropdown',
      component: FormlyContentDropdownComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'datepicker',
      component: FormlyDatepickerComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'dateTimePicker',
      component: FormlyDateTimePickerComponent,
      wrappers: ['basin-custom-form-field'],
    },
    { name: 'divider', component: FormlyDividerComponent },
    {
      name: 'dropdown',
      component: FormlyDropdownComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'fileUpload',
      component: FormlyFileUploadComponent,
      wrappers: ['basin-custom-form-field'],
      extends: 'input',
      defaultOptions: {
        validators: {
          file: file.expression,
        },
      },
    },
    { name: 'fileUploadBox', component: FormlyFileUploadBoxComponent },
    {
      name: 'inputNumber',
      component: FormlyInputNumberComponent,
      wrappers: ['basin-custom-form-field'],
    },
    { name: 'inputSwitch', component: FormlyInputSwitchComponent },
    {
      name: 'inputWithSelector',
      component: FormlyInputWithSelectorComponent,
      defaultOptions: {
        validators: {
          inputWithSelector: inputWithSelector.expression,
        },
      },
      wrappers: ['basin-custom-form-field'],
    },
    { name: 'label', component: FormlyLabelComponent },
    {
      name: 'multiselect',
      component: FormlyMultiselectComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'multiselect-group',
      component: FormlyMultiselectGroupedComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'password',
      component: FormlyPasswordComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'phone',
      wrappers: ['basin-custom-form-field'],
      component: FormlyPhoneComponent,
      defaultOptions: {
        validators: {
          phone: phone.expression,
        },
      },
    },
    { name: 'radioButton', component: FormlyRadioButtonComponent },
    { name: 'repeat', component: FormlyRepeatComponent },
    { name: 'repeatSingle', component: FormlyRepeatSingleComponent },
    { name: 'stepper', component: FormlyStepperComponent },
    { name: 'searchbox', component: FormlySearchboxComponent },
    { name: 'slider', component: FormlySliderComponent },
    {
      name: 'basin-textarea',
      wrappers: ['basin-custom-form-field'],
      component: FormlyTextareaComponent,
    },
    { name: 'text', component: FormlyTextComponent },
    {
      name: 'textbox',
      wrappers: ['basin-custom-form-field'],
      component: FormlyTextboxComponent,
    },
    {
      name: 'textbox-copy',
      component: FormlyTextboxWithCopyComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'textbox-units',
      component: FormlyTextboxWithUnitsComponent,
      wrappers: ['basin-custom-form-field'],
      defaultOptions: {
        validators: {
          minmax: minmax.expression,
          numeric: numeric.expression,
        },
      },
    },
    {
      name: 'timeframePicker',
      component: FormlyTimeframePickerComponent,
      wrappers: ['basin-custom-form-field'],
      defaultOptions: {
        validators: {
          timeframe: timeframe.expression,
        },
      },
    },
    {
      name: 'timepicker',
      component: FormlyTimepickerComponent,
      wrappers: ['basin-custom-form-field'],
    },
    {
      name: 'alphaOnly',
      wrappers: ['basin-custom-form-field'],
      defaultOptions: {
        validators: {
          alphaOnly: alphaOnly.expression,
        },
      },
    },
    {
      name: 'postalcode',
      wrappers: ['basin-custom-form-field'],
      defaultOptions: {
        validators: {
          postalcode: postalCode.expression,
        },
      },
    },
    {
      name: 'numericRange',
      wrappers: ['basin-custom-form-field'],
      component: FormlyNumericRangeComponent,
      defaultOptions: {
        validators: {
          numericRange: numericRange.expression,
        },
      },
    },
  ],
  wrappers: [
    {
      name: 'basin-custom-form-field',
      component: CustomFormFieldComponent,
    },
  ],
}

@NgModule({
  declarations: [
    FormlyAttendeesComponent,
    FormlyCheckboxComponent,
    FormlyCheckboxGroupComponent,
    FormlyChipSelectorComponent,
    FormlyChipsComponent,
    FormlyColorPickerComponent,
    FormlyContentDropdownComponent,
    FormlyDatepickerComponent,
    FormlyDateTimePickerComponent,
    FormlyDividerComponent,
    FormlyDropdownComponent,
    FormlyFileUploadComponent,
    FormlyFileUploadBoxComponent,
    FormlyInputNumberComponent,
    FormlyInputSwitchComponent,
    FormlyLabelComponent,
    FormlyMultiselectComponent,
    FormlyMultiselectGroupedComponent,
    FormlyNumericRangeComponent,
    FormlyPasswordComponent,
    FormlyPhoneComponent,
    FormlyRadioButtonComponent,
    FormlyRepeatComponent,
    FormlyRepeatSingleComponent,
    FormlySearchboxComponent,
    FormlySelectOptionsPipe,
    FormlySliderComponent,
    FormlyTextComponent,
    FormlyTextareaComponent,
    FormlyTextboxComponent,
    FormlyTextboxWithCopyComponent,
    FormlyTextboxWithUnitsComponent,
    FormlyTimepickerComponent,
    FormlyTimeframePickerComponent,
    FormlyStepperComponent,
    CustomFormFieldComponent,
    FormlyInputWithSelectorComponent,
  ],
  imports: [
    AutoCompleteModule,
    AvatarModule,
    ButtonModule,
    CalendarModule,
    CardModule,
    CheckboxModule,
    ChipSelectorModule,
    ChipsModule,
    ColorPickerModule,
    CommonModule,
    DividerModule,
    DragDropModule,
    DropdownModule,
    FileUploadModule,
    FormlyModule,
    FormsModule,
    HttpClientModule,
    InputMaskModule,
    InputNumberModule,
    InputSwitchModule,
    InputTextareaModule,
    InputTextModule,
    InputWithSelectorModule,
    ListItemModule,
    MenuModule,
    MultiSelectModule,
    NumericRangeModule,
    OverlayModule,
    PasswordModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    ReactiveFormsModule,
    SearchboxModule,
    SliderModule,
    StepperModule,
    StepsModule,
    TextboxWithUnitsModule,
    TimeframePickerModule,
  ],
  exports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyAttendeesComponent,
    FormlyCheckboxComponent,
    FormlyCheckboxGroupComponent,
    FormlyChipSelectorComponent,
    FormlyChipsComponent,
    FormlyColorPickerComponent,
    FormlyContentDropdownComponent,
    FormlyDatepickerComponent,
    FormlyDateTimePickerComponent,
    FormlyDividerComponent,
    FormlyDropdownComponent,
    FormlyFileUploadComponent,
    FormlyFileUploadBoxComponent,
    FormlyInputNumberComponent,
    FormlyInputSwitchComponent,
    FormlyLabelComponent,
    FormlyMultiselectComponent,
    FormlyMultiselectGroupedComponent,
    FormlyNumericRangeComponent,
    FormlyPasswordComponent,
    FormlyPhoneComponent,
    FormlyRadioButtonComponent,
    FormlyRepeatComponent,
    FormlyRepeatSingleComponent,
    FormlySearchboxComponent,
    FormlySelectOptionsPipe,
    FormlySliderComponent,
    FormlyStepperComponent,
    FormlyTextComponent,
    FormlyTextareaComponent,
    FormlyTextboxComponent,
    FormlyTextboxWithCopyComponent,
    FormlyTextboxWithUnitsComponent,
    FormlyTimepickerComponent,
    FormlyTimeframePickerComponent,
    CustomFormFieldComponent,
  ],
})
export class BasinFormlyModule {}
